import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import {
  ManagerProgressionTaskState,
  TaskLineupType,
} from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { WidgetChip } from '@sorare/core/src/atoms/ui/Chip/WidgetChip/index.tsx';
import { WidgetButton } from '@sorare/core/src/atoms/ui/WidgetButton/index.tsx';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs/index.tsx';
import { idFromObject } from '@sorare/core/src/gql/idFromObject.ts';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog.ts';
import {
  LIFECYCLE,
  useLifecycle,
} from '@sorare/core/src/hooks/useLifecycle.ts';
import { isType } from '@sorare/core/src/lib/gql/index.ts';

import { getProgress } from '#src/components/series/lineup/GuessTheScoreTaskLineup/GuessTheScoreTaskLineupProgress/index.tsx';

import { GuessTheScoreIntroDialog } from './GuessTheScoreIntroDialog/index.tsx';
import { VsIcon } from './VsIcon/index.tsx';
import type { GuessTheScoreBadge_taskInterface } from './__generated__/index.graphql.ts';
import badgeBg from './assets/guess-the-score-bg.webp';

type GuessTheScoreBadgeState = 'new' | 'inProgress' | 'claimable' | 'failed';

const Circle = styled(Horizontal).attrs({ center: true })`
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
  border-radius: 50%;
  background: url(${badgeBg}) no-repeat center / cover;
  padding: 2px;
  position: relative;
  overflow: hidden;
`;
const PlayerImg = styled.img`
  width: 40px;
  object-fit: cover;
  position: absolute;
  top: 8px;

  &.home {
    left: -8px;
  }
  &.away {
    right: -8px;
  }
`;
const Vs = styled.div`
  position: relative;
  z-index: 1;
  & > * {
    height: 30px;
  }
`;

const getState = (
  task: GuessTheScoreBadge_taskInterface
): GuessTheScoreBadgeState | null => {
  if (!isType(task, 'GuessTheScoreTask')) {
    return 'new';
  }
  if (task.aasmState === ManagerProgressionTaskState.COMPLETED) {
    return 'claimable';
  }
  if (task.taskLineups?.[0]?.aasmState === TaskLineupType.FAILED) {
    return 'failed';
  }
  if (task.taskLineups?.length) {
    return 'inProgress';
  }
  return null;
};

const GuessTheScoreChip = ({
  state,
  score,
  target,
}: {
  state: GuessTheScoreBadgeState | null;
  score: number | null;
  target: number | null;
}) => {
  if (state === 'claimable') {
    return <WidgetChip state="reward" />;
  }
  if (state === 'failed') {
    return <WidgetChip state="fail" />;
  }
  if (state === 'inProgress') {
    return (
      <WidgetChip state="progress" progress={getProgress(score, target)}>
        {Math.round(score ?? 0)}
      </WidgetChip>
    );
  }
  if (state === 'new') {
    return <WidgetChip state="new" />;
  }
  return null;
};

type Props = {
  task: GuessTheScoreBadge_taskInterface;
};

export const GuessTheScoreBadge = ({ task }: Props) => {
  const openDialog = useOpenDialog();
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const [showIntro, setShowIntro] = useState(false);

  if (!isType(task, 'GuessTheScoreTask')) {
    return null;
  }

  const openTaskDialog = () => {
    openDialog(DialogKey.guessTheScoreTask, idFromObject(task.id));
  };

  const { leftPlayer, rightPlayer } = task;
  const sawIntro =
    lifecycle?.[LIFECYCLE.goldenScoreIntroLastGameSeen] === task.game.id;
  const state = sawIntro ? getState(task) : 'new';
  const liveLineup = task.taskLineups?.[0] ?? null;
  const liveScore = liveLineup?.score ?? null;
  const target = liveLineup?.targetScore ?? null;

  return (
    <>
      <WidgetButton
        onClick={() => {
          if (!sawIntro) {
            setShowIntro(true);
            updateLifecycle(
              LIFECYCLE.goldenScoreIntroLastGameSeen,
              task.game.id
            );
            return;
          }
          openTaskDialog();
        }}
        chip={
          <GuessTheScoreChip state={state} score={liveScore} target={target} />
        }
      >
        <Circle>
          {!!leftPlayer && (
            <PlayerImg
              className="home"
              src={leftPlayer.squaredPictureUrl}
              alt=""
              role="presentation"
            />
          )}
          {!!rightPlayer && (
            <PlayerImg
              className="away"
              src={rightPlayer.squaredPictureUrl}
              alt=""
              role="presentation"
            />
          )}
          <Vs>
            <VsIcon />
          </Vs>
        </Circle>
      </WidgetButton>
      <GuessTheScoreIntroDialog
        open={showIntro}
        task={task}
        onClose={() => setShowIntro(false)}
        onJoin={() => {
          setShowIntro(false);
          openTaskDialog();
        }}
      />
    </>
  );
};

GuessTheScoreBadge.fragments = {
  taskInterface: gql`
    fragment GuessTheScoreBadge_taskInterface on TaskInterface {
      id
      ... on GuessTheScoreTask {
        id
        aasmState
        ...GuessTheScoreIntroDialog_guessTheScoreTask
        taskLineups {
          id
          aasmState
          score
          targetScore
        }
        game {
          id
        }
        leftPlayer {
          slug
          squaredPictureUrl
        }
        rightPlayer {
          slug
          squaredPictureUrl
        }
      }
    }
    ${GuessTheScoreIntroDialog.fragments.guessTheScoreTask}
  ` as TypedDocumentNode<GuessTheScoreBadge_taskInterface>,
};
